import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import paths from 'src/paths';
import type { DraftPurchaseOrderFreight as DraftFreight } from 'src/api/rpc/hooks/procurement/useFreightOptions';
import { termsKindLabel } from 'src/pages/authenticated/procurement/freight-options/freightOptionLabels';
import { DraftFreightEditor } from './DraftFreightEditor';
import { DraftFreightLoad } from './DraftFreightLoad';
import { describeCode, termsKindOf } from './draftFreightFormat';
import {
  useDraftFreightPanel,
  type DraftFreightPanel,
} from './useDraftFreightPanel';

/**
 * The freight a draft purchase order goes out with (GEN-3281): carrier,
 * freight terms and who pays, carrier account and the lines printed on the
 * PO. Until the PO is sent, a buyer who can edit purchase orders picks one of
 * the branch's confirmed freight options here.
 */
export function DraftPurchaseOrderFreight({
  draftPurchaseOrderId,
}: {
  draftPurchaseOrderId: string;
}) {
  const [editing, setEditing] = useState(false);
  const panel = useDraftFreightPanel(draftPurchaseOrderId);
  if (!panel) return null;
  return (
    <section className="rounded-md border p-3 text-xs">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          <T context="draft PO section heading">Freight</T>
        </h3>
        <div className="flex items-center gap-2">
          <FreightChoiceBadge choice={panel.freight.freightChoice} />
          {panel.canEdit && !editing ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
            >
              <T context="draft PO freight action">Change</T>
            </Button>
          ) : null}
        </div>
      </div>
      {editing ? (
        <DraftFreightEditor
          freight={panel.freight}
          options={panel.options}
          onDone={() => setEditing(false)}
        />
      ) : (
        <FreightSummary panel={panel} />
      )}
      <DraftFreightLoad freight={panel.freight} />
      {panel.canReadOptions ? <FreightOptionsLink /> : null}
    </section>
  );
}

function FreightOptionsLink() {
  return (
    <div className="mt-2">
      <Link
        to={paths.authenticated.procurement.freightOptions}
        className="text-muted-foreground underline"
      >
        <T context="link to the branch freight options page">
          Branch freight options
        </T>
      </Link>
    </div>
  );
}

function FreightChoiceBadge({
  choice,
}: {
  choice: DraftFreight['freightChoice'];
}) {
  if (choice === 'suggested')
    return (
      <Badge variant="secondary">
        <T context="draft PO freight: picked by the branch's freight rules">
          Suggested
        </T>
      </Badge>
    );
  if (choice === 'override')
    return (
      <Badge variant="outline">
        <T context="draft PO freight: the buyer chose a different freight">
          Changed by buyer
        </T>
      </Badge>
    );
  return null;
}

function FreightSummary({ panel }: { panel: DraftFreightPanel }) {
  const gt = useGT();
  const { freight } = panel;
  if (!freight.shipViaCode)
    return (
      <p className="text-muted-foreground">
        <T context="draft PO freight empty state">
          No freight chosen. The vendor's default applies when the PO is sent.
        </T>
      </p>
    );
  const kind = termsKindOf(freight.freightTermsCode, panel.freightTermsCodes);
  const kindSuffix = kind ? ` (${termsKindLabel(gt, kind)})` : '';
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
      <Fact label={<T context="draft PO freight fact">Carrier</T>}>
        {describeCode(freight.shipViaCode, panel.shipViaCodes)}
      </Fact>
      <Fact label={<T context="draft PO freight fact">Freight terms</T>}>
        {describeCode(freight.freightTermsCode, panel.freightTermsCodes)}
        {kindSuffix}
      </Fact>
      {freight.carrierAccount ? (
        <Fact label={<T context="draft PO freight fact">Carrier account</T>}>
          <span className="font-mono">{freight.carrierAccount}</span>
        </Fact>
      ) : null}
      <InstructionLines lines={freight.instructions} />
    </dl>
  );
}

function Fact({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function InstructionLines({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <div className="col-span-full">
      <dt className="text-muted-foreground">
        <T context="draft PO freight fact: header comment lines printed on the PO">
          Shipping instructions
        </T>
      </dt>
      <dd className="font-mono text-[11px] leading-tight whitespace-pre-line">
        {lines.join('\n')}
      </dd>
    </div>
  );
}
